import { useEffect, useMemo, useRef, useState } from 'react';
import { api, Button, Card, ConfirmDialog, Field, InfoTip, PillButton, apiErrorMessage, formatMoney, brandLocalDate, rebaseDatePrefill, useBrand } from '@aralere/shared-ui';
import type {
  Business,
  Client,
  CreateProjectRequest,
  ProjectStatus,
  ProjectSubcontractor,
  ProjectWithTotals,
  UpdateProjectRequest,
  Vendor,
} from '@aralere/shared-types';

// Projects admin (spec §10.3; Stage-3 slice 6 — s43 record §10). Projects are partitioned by CLIENT
// (clientId is the partition key, IMMUTABLE after create — financial history never re-homes), so the
// page is client-scoped: pick a client, see their projects with the COMPUTED totals (invoiced / paid
// / unbilled / remaining — an API projection, never persisted; mostly zeros until Stage 4), and
// create/edit projects. Status: active | completed | on-hold. Optional fields per spec: budget,
// estimated hours, type, location, point of contact, working hours, a single subcontractor (an
// active subcontractor vendor; masked client-facing as "Additional labor costs" later — the admin
// always sees the real vendor) + its cost. "Generate invoice" (Stage-4 slice 3, §10.3 progress
// billing) shows on a project with unbilled charges: one click creates a DRAFT invoice for the
// project's client with ALL its current unbilled time / mileage / expense lines pulled on (sources
// marked billed at pull, §7.11), then lands in the invoice editor with that draft open. Any project
// status may bill — the active-only gate applies to NEW entry links, not to billing logged work.
// Delete is
// allowed ONLY while nothing references the project (server 409 project_in_use otherwise) — mark it
// completed instead. Required fields carry the red asterisk via the shared Field (platform
// standard); mobile-first (stacked cards under md, table from md up).

const inputClass = 'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]';

const fmtMoney = formatMoney; // shared thousands-separated formatter (s58)

/** YYYY-MM-DD -> MM-DD-YYYY. */
function displayDate(iso: string | null): string {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-');
  return `${m}-${d}-${y}`;
}

const STATUS_LABEL: Record<ProjectStatus, string> = {
  active: 'Active',
  completed: 'Completed',
  'on-hold': 'On hold',
};
const STATUS_TONE: Record<ProjectStatus, string> = {
  active: 'bg-[var(--tint-green-bg)] text-[var(--tint-green-text)]',
  completed: 'bg-[var(--tint-slate-bg)] text-[var(--tint-slate-text)]',
  'on-hold': 'bg-[var(--tint-warn-bg)] text-[var(--tint-warn-text)]',
};

interface SubForm {
  vendorId: string;
  cost: string;
}

interface FormState {
  name: string;
  status: ProjectStatus;
  startDate: string;
  endDate: string;
  budget: string;
  estimatedHours: string;
  projectType: string;
  location: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  workingHours: string;
  subcontractors: SubForm[];
}

// 04 #136: the start date prefills to today on the brand clock of the client's brand (02 s177).
const emptyForm = (timezone: string | null): FormState => ({
  name: '',
  status: 'active',
  startDate: brandLocalDate(timezone),
  endDate: '',
  budget: '',
  estimatedHours: '',
  projectType: '',
  location: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  workingHours: '',
  subcontractors: [],
});

function formFromProject(p: ProjectWithTotals): FormState {
  return {
    name: p.name,
    status: p.status,
    startDate: p.startDate,
    endDate: p.endDate ?? '',
    budget: p.budget != null ? String(p.budget) : '',
    estimatedHours: p.estimatedHours != null ? String(p.estimatedHours) : '',
    projectType: p.projectType ?? '',
    location: p.location ?? '',
    contactName: p.pointOfContact?.name ?? '',
    contactEmail: p.pointOfContact?.email ?? '',
    contactPhone: p.pointOfContact?.phone ?? '',
    workingHours: p.workingHours ?? '',
    subcontractors: (p.subcontractors ?? []).map((r) => ({ vendorId: r.vendorId, cost: r.cost != null ? String(r.cost) : '' })),
  };
}

/** Optional money field: '' -> null; else a parsed non-negative 2dp number, or an error string. */
function moneyField(v: string, label: string): { value: number | null } | { error: string } {
  if (!v.trim()) return { value: null };
  const n = parseFloat(v);
  if (!Number.isFinite(n) || n < 0) return { error: `${label} must be a non-negative amount (or blank).` };
  return { value: Math.round(n * 100) / 100 };
}

export function AdminProjects(): JSX.Element {
  const { brand: portalBrand } = useBrand();
  const portalTz = portalBrand?.timezone ?? null;
  const [clients, setClients] = useState<Client[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [clientId, setClientId] = useState('');
  const [projects, setProjects] = useState<ProjectWithTotals[]>([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ProjectWithTotals | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProjectWithTotals | null>(null);
  const [deleting, setDeleting] = useState(false);
  /** The project currently generating its progress invoice (slice 3), or null. */
  const [generating, setGenerating] = useState<string | null>(null);

  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);
  const subcontractors = useMemo(
    () => vendors.filter((v) => v.isSubcontractor),
    [vendors],
  );
  const vendorLabel = (id: string | null | undefined): string =>
    id ? vendors.find((v) => v.id === id)?.displayName ?? id : '-';

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [c, v, b] = await Promise.all([api.admin.listClients(), api.admin.listVendors(), api.admin.listBusinesses()]);
        if (!alive) return;
        setClients(c.clients.map((r) => r.client)); // listClients returns enriched rows { client, ... }
        setVendors(v.vendors);
        setBusinesses(b.businesses);
      } catch (e) {
        if (alive) setError(apiErrorMessage(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const load = async (cid: string): Promise<void> => {
    setListLoading(true);
    setError(null);
    try {
      // No client → the all-active overview across every client (s58); a client → that client's projects.
      const res = await api.admin.listProjects(cid || undefined);
      setProjects(res.projects);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    void load(clientId);
  }, [clientId]);

  const update = (patch: Partial<FormState>): void => setForm((f) => (f ? { ...f, ...patch } : f));
  const addSub = (): void => setForm((f) => (f ? { ...f, subcontractors: [...f.subcontractors, { vendorId: '', cost: '' }] } : f));
  const updateSub = (idx: number, patch: Partial<SubForm>): void =>
    setForm((f) => (f ? { ...f, subcontractors: f.subcontractors.map((r, i) => (i === idx ? { ...r, ...patch } : r)) } : f));
  const removeSub = (idx: number): void =>
    setForm((f) => (f ? { ...f, subcontractors: f.subcontractors.filter((_, i) => i !== idx) } : f));

  /** 04 #136: the timezone a NEW project's start date is taken in: the client's brand (its
   *  `timezone`, absent = the platform default), else the portal brand. */
  const clientTz = (cid: string): string | null => {
    const c = clients.find((x) => x.id === cid);
    const b = c ? businesses.find((x) => x.id === c.primaryBusinessId) : undefined;
    return b ? b.timezone ?? null : portalTz;
  };
  /** The zone an open create form's start date was last computed in. */
  const prefillTzRef = useRef<string | null>(null);
  // 04 #136: a create posts to the PAGE client, so if that client (or its brand data) changes while
  // the form is open, an untouched start date follows the new owning brand; an edit form never moves.
  useEffect(() => {
    const to = clientTz(clientId);
    const from = prefillTzRef.current;
    prefillTzRef.current = to;
    if (from === to || editing) return;
    setForm((f) => (f ? { ...f, startDate: rebaseDatePrefill(f.startDate, from, to) } : f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, clients, businesses, portalTz]);

  const openCreate = (): void => {
    setEditing(null);
    prefillTzRef.current = clientTz(clientId);
    setForm(emptyForm(prefillTzRef.current));
    setFormError(null);
    setFormOpen(true);
  };
  const openEdit = (p: ProjectWithTotals): void => {
    setEditing(p);
    setForm(formFromProject(p));
    setFormError(null);
    setFormOpen(true);
  };
  const closeForm = (): void => {
    setFormOpen(false);
    setForm(null);
    setEditing(null);
    setFormError(null);
  };

  const doSave = async (): Promise<void> => {
    if (!form) return;
    // Edit targets the project's own client; create targets the page-selected client.
    const targetClientId = editing ? editing.clientId : clientId;
    if (!targetClientId) return setFormError('Select a client before creating a project.');
    if (!form.name.trim()) return setFormError('A project name is required.');
    if (!form.startDate) return setFormError('A start date is required.');
    if (form.endDate && form.endDate < form.startDate) return setFormError('The end date cannot be before the start date.');
    const budget = moneyField(form.budget, 'Budget');
    if ('error' in budget) return setFormError(budget.error);
    let estimatedHours: number | null = null;
    if (form.estimatedHours.trim()) {
      const n = parseFloat(form.estimatedHours);
      if (!Number.isFinite(n) || n < 0) return setFormError('Estimated hours must be a non-negative number (or blank).');
      estimatedHours = n;
    }
    if ((form.contactName.trim() || form.contactEmail.trim() || form.contactPhone.trim()) &&
        (!form.contactName.trim() || !form.contactPhone.trim())) {
      return setFormError('A point-of-contact needs a name and a phone number (email is optional).');
    }
    const pointOfContact = form.contactName.trim()
      ? {
          name: form.contactName.trim(),
          phone: form.contactPhone.trim(),
          email: form.contactEmail.trim() ? form.contactEmail.trim() : null,
        }
      : null;

    // Subcontractors: each needs a vendor; costs are optional; no duplicate vendor.
    const subVendors = new Set<string>();
    const subcontractors: ProjectSubcontractor[] = [];
    for (const r of form.subcontractors) {
      if (!r.vendorId) return setFormError('Each subcontractor row needs a vendor (remove the empty row, or pick one).');
      if (subVendors.has(r.vendorId)) return setFormError('A subcontractor is listed more than once.');
      subVendors.add(r.vendorId);
      const c = moneyField(r.cost, 'Subcontractor cost');
      if ('error' in c) return setFormError(c.error);
      subcontractors.push({ vendorId: r.vendorId, cost: c.value });
    }

    const common = {
      name: form.name.trim(),
      status: form.status,
      startDate: form.startDate,
      endDate: form.endDate ? form.endDate : null,
      budget: budget.value,
      estimatedHours,
      projectType: form.projectType.trim() ? form.projectType.trim() : null,
      location: form.location.trim() ? form.location.trim() : null,
      pointOfContact,
      workingHours: form.workingHours.trim() ? form.workingHours.trim() : null,
      subcontractors,
    };

    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        const body: UpdateProjectRequest = common;
        await api.admin.updateProject(editing.id, targetClientId, body);
      } else {
        const body: CreateProjectRequest = { ...common, clientId: targetClientId };
        await api.admin.createProject(body);
      }
      closeForm();
      await load(clientId);
    } catch (e) {
      setFormError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.admin.deleteProject(deleteTarget.id, deleteTarget.clientId);
      setDeleteTarget(null);
      await load(clientId);
    } catch (e) {
      setError(apiErrorMessage(e));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  /** "Generate invoice" (§10.3): one server op creates the draft AND pulls all the project's
   *  current unbilled items, then navigates into the invoice editor with that draft open. */
  const doGenerateInvoice = async (p: ProjectWithTotals): Promise<void> => {
    if (generating) return;
    setGenerating(p.id);
    setError(null);
    try {
      const res = await api.admin.generateProjectInvoice({ clientId: p.clientId, projectId: p.id });
      window.location.assign(
        `/admin/invoices?client=${encodeURIComponent(p.clientId)}&open=${encodeURIComponent(res.invoice.id)}`,
      );
      // no state reset — we're navigating away
    } catch (e) {
      setError(apiErrorMessage(e));
      setGenerating(null);
    }
  };

  const totalsSummary = (p: ProjectWithTotals): JSX.Element => (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-muted)]">
      <span>Invoiced <span className="font-semibold text-[var(--color-text)]">{fmtMoney(p.totals.invoiced)}</span></span>
      <span>Paid <span className="font-semibold text-[var(--color-text)]">{fmtMoney(p.totals.paid)}</span></span>
      <span>Unbilled <span className="font-semibold text-[var(--color-text)]">{fmtMoney(p.totals.unbilled)}</span></span>
      <span>
        Remaining{' '}
        <span className="font-semibold text-[var(--color-text)]">
          {p.totals.remaining != null ? fmtMoney(p.totals.remaining) : '-'}
        </span>
        {p.totals.remaining == null ? <InfoTip text="Remaining budget = budget − invoiced − unbilled. Set a budget on the project to see it." /> : null}
      </span>
    </div>
  );

  if (loading) return <div className="p-6 text-sm text-[var(--color-muted)]">Loading…</div>;

  const client = clientId ? clientById.get(clientId) ?? null : null;

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-[var(--color-text)]">Projects</h1>
        {clientId ? <Button onClick={openCreate}>Create a project</Button> : null}
      </div>

      <Card className="relative z-20">
        <div className="flex flex-wrap items-end gap-3 p-4">
          <Field label="Client" tip="Leave on 'All active projects' to browse every client's active work. Pick a client to see all of that client's projects (including completed and on-hold) and to add one. A project's client cannot change after creation.">
            <select className={`${inputClass} md:min-w-[260px]`} value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">All active projects</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.displayName}</option>
              ))}
            </select>
          </Field>
        </div>
      </Card>

      {error ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-3 text-sm text-[var(--tint-red-text)]">{error}</div> : null}

      {formOpen && form ? (
        <Card>
          <div className="space-y-3 p-4">
            <h2 className="text-base font-semibold text-[var(--color-text)]">
              {editing ? `Edit project: ${editing.name}` : `New project${client ? ` for ${client.displayName}` : ''}`}
            </h2>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Name" required>
                <input className={inputClass} value={form.name} onChange={(e) => update({ name: e.target.value })} maxLength={200} />
              </Field>
              <Field label="Status" required>
                <select className={inputClass} value={form.status} onChange={(e) => update({ status: e.target.value as ProjectStatus })}>
                  <option value="active">Active</option>
                  <option value="on-hold">On hold</option>
                  <option value="completed">Completed</option>
                </select>
              </Field>
              <Field label="Start date" required>
                <input type="date" className={inputClass} value={form.startDate} onChange={(e) => update({ startDate: e.target.value })} />
              </Field>
              <Field label="End date">
                <input type="date" className={inputClass} value={form.endDate} onChange={(e) => update({ endDate: e.target.value })} />
              </Field>
              <Field label="Budget ($)" tip="Optional. When set, the Remaining total shows budget − invoiced − unbilled.">
                <input className={inputClass} inputMode="decimal" value={form.budget} onChange={(e) => update({ budget: e.target.value })} />
              </Field>
              <Field label="Estimated hours">
                <input className={inputClass} inputMode="decimal" value={form.estimatedHours} onChange={(e) => update({ estimatedHours: e.target.value })} />
              </Field>
              <Field label="Project type">
                <input className={inputClass} value={form.projectType} onChange={(e) => update({ projectType: e.target.value })} maxLength={100} placeholder="e.g. infrastructure" />
              </Field>
              <Field label="Location">
                <input className={inputClass} value={form.location} onChange={(e) => update({ location: e.target.value })} maxLength={200} />
              </Field>
              <Field label="Contact name" tip="Optional point of contact for the project. If set, a name and phone are required; email is optional.">
                <input className={inputClass} value={form.contactName} onChange={(e) => update({ contactName: e.target.value })} maxLength={120} />
              </Field>
              <Field label="Contact email">
                <input className={inputClass} type="email" value={form.contactEmail} onChange={(e) => update({ contactEmail: e.target.value })} maxLength={200} />
              </Field>
              <Field label="Contact phone" tip="Required when a contact is set.">
                <input className={inputClass} value={form.contactPhone} onChange={(e) => update({ contactPhone: e.target.value })} maxLength={40} />
              </Field>
              <Field label="Working hours">
                <input className={inputClass} value={form.workingHours} onChange={(e) => update({ workingHours: e.target.value })} maxLength={200} placeholder="e.g. Mon-Fri 8-5" />
              </Field>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-sm font-medium text-[var(--color-text)]">
                  Subcontractors
                  <InfoTip text="Optional. Add one or more subcontractor vendors, each with an optional cost. Shown to clients later only as 'Additional labor costs'. Inactive vendors can't be newly added; existing ones are kept." />
                </div>
                <Button variant="secondary" onClick={addSub} disabled={saving}>Add subcontractor</Button>
              </div>
              {form.subcontractors.length === 0 ? (
                <p className="text-xs text-[var(--color-muted)]">None. Add one if a subcontractor works this project.</p>
              ) : (
                form.subcontractors.map((row, idx) => {
                  const chosen = new Set(form.subcontractors.filter((_, i) => i !== idx).map((r) => r.vendorId));
                  return (
                    <div key={idx} className="grid gap-2 md:grid-cols-[2fr,1fr,auto] md:items-end">
                      <Field label="Vendor">
                        <select className={inputClass} value={row.vendorId} onChange={(e) => updateSub(idx, { vendorId: e.target.value })}>
                          <option value="">Choose a subcontractor…</option>
                          {subcontractors
                            .filter((v) => (v.active || v.id === row.vendorId) && !chosen.has(v.id))
                            .map((v) => (
                              <option key={v.id} value={v.id}>{v.displayName}{v.active ? '' : ' (inactive)'}</option>
                            ))}
                        </select>
                      </Field>
                      <Field label="Cost ($)">
                        <input className={inputClass} inputMode="decimal" value={row.cost} onChange={(e) => updateSub(idx, { cost: e.target.value })} />
                      </Field>
                      <PillButton tone="red" onClick={() => removeSub(idx)}>Remove</PillButton>
                    </div>
                  );
                })
              )}
            </div>
            {formError ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{formError}</div> : null}
            <div className="flex gap-2">
              <Button onClick={() => void doSave()} disabled={saving}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Create project'}</Button>
              <Button variant="ghost" onClick={closeForm} disabled={saving}>Cancel</Button>
            </div>
          </div>
        </Card>
      ) : null}

      {listLoading ? (
        <div className="p-4 text-sm text-[var(--color-muted)]">Loading projects…</div>
      ) : projects.length === 0 ? (
        <div className="rounded-md border border-dashed border-[rgb(var(--edge-rgb)/0.10)] p-6 text-center text-sm text-[var(--color-muted)]">
          {clientId
            ? `No projects yet${client ? ` for ${client.displayName}` : ''}. Create one to start tagging time, mileage, and expenses to it.`
            : 'No active projects across any client. Choose a client above to create one.'}
        </div>
      ) : (
        <div className="space-y-3">
          {!clientId ? (
            <p className="text-xs text-[var(--color-muted)]">Showing all active projects across every client. Choose a client to see that client's projects (including completed and on-hold) and to add a project.</p>
          ) : null}
          {projects.map((p) => (
            <Card key={p.id}>
              <div className="space-y-2 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-[var(--color-text)]">{p.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[p.status]}`}>{STATUS_LABEL[p.status]}</span>
                    {!clientId ? (
                      <span className="text-xs text-[var(--color-muted)]">· {clientById.get(p.clientId)?.displayName ?? p.clientId}</span>
                    ) : null}
                  </div>
                  <div className="flex gap-2">
                    {p.totals.unbilled > 0 ? (
                      <PillButton tone="green" onClick={() => void doGenerateInvoice(p)}>
                        {generating === p.id ? 'Generating…' : 'Generate invoice'}
                      </PillButton>
                    ) : null}
                    <PillButton onClick={() => openEdit(p)}>Edit</PillButton>
                    <PillButton tone="red" onClick={() => setDeleteTarget(p)}>Delete</PillButton>
                  </div>
                </div>
                <div className="text-xs text-[var(--color-muted)]">
                  {displayDate(p.startDate)} → {displayDate(p.endDate)}
                  {p.projectType ? ` · ${p.projectType}` : ''}
                  {p.location ? ` · ${p.location}` : ''}
                  {p.estimatedHours != null ? ` · est. ${p.estimatedHours}h` : ''}
                  {p.budget != null ? ` · budget ${fmtMoney(p.budget)}` : ''}
                </div>
                {p.pointOfContact ? (
                  <div className="text-xs text-[var(--color-muted)]">
                    Contact: {p.pointOfContact.name} · {p.pointOfContact.phone}
                    {p.pointOfContact.email ? ` · ${p.pointOfContact.email}` : ''}
                    {p.workingHours ? ` · ${p.workingHours}` : ''}
                  </div>
                ) : p.workingHours ? (
                  <div className="text-xs text-[var(--color-muted)]">Working hours: {p.workingHours}</div>
                ) : null}
                {p.subcontractors && p.subcontractors.length > 0 ? (
                  <div className="text-xs text-[var(--color-muted)]">
                    {p.subcontractors.length === 1 ? 'Subcontractor: ' : 'Subcontractors: '}
                    {p.subcontractors.map((r, i) => (
                      <span key={r.vendorId}>
                        {i > 0 ? ', ' : ''}{vendorLabel(r.vendorId)}{r.cost != null ? ` (${fmtMoney(r.cost)})` : ''}
                      </span>
                    ))}
                    <InfoTip text="Internal only. Client-facing views show subcontractor spend as 'Additional labor costs' (built with the client project views at a later stage)." />
                  </div>
                ) : null}
                {totalsSummary(p)}
              </div>
            </Card>
          ))}
        </div>
      )}

      {deleteTarget ? (
        <ConfirmDialog
          open={true}
          title="Delete project?"
          confirmLabel="Delete"
          confirmVariant="danger"
          busy={deleting}
          onConfirm={doDelete}
          onCancel={() => setDeleteTarget(null)}
        >
          <p>
            Delete "{deleteTarget.name}"? This only works while no time, mileage, or expenses are linked to it
            (mark it completed instead). This cannot be undone.
          </p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
